import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowRight,
  ChevronRight,
  Zap,
  Globe,
  Cpu,
  Shield,
  Leaf,
  Lightbulb,
  FileText,
  MessageSquare,
  BookOpen,
  Send,
  Linkedin,
  Twitter,
  Mail,
  Menu,
  X,
  Search,
  Rss } from
"lucide-react";
import { allArticles, newsData, thoughtLeadershipData, getAllArticles } from "../data/articles";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const searchRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const fullArticles = getAllArticles();
      const results = fullArticles.filter(
        (article) =>
          article.title.toLowerCase().includes(query) ||
          article.category.toLowerCase().includes(query) ||
          (article.summary && article.summary.toLowerCase().includes(query))
      );
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  // Keyboard shortcut: Ctrl+K / Cmd+K to open search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
      if (e.key === "Escape") {
        setSearchOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleResultClick = (slug) => {
    navigate(`/article/${slug}`);
    setSearchOpen(false);
    setSearchQuery("");
  };

  const navLinks = [
  { href: "#news", label: "Latest News" },
  { href: "#insights", label: "Insights" },
  { href: "#about", label: "About" },
  { href: "#services", label: "Services" }];


  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? "glass border-b border-white/5" : "bg-transparent"}`
      }>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20">
          <a
            href="#"
            data-testid="logo-link"
            className="text-2xl font-bold font-['Playfair_Display']">

            Future<span className="text-[hsl(var(--primary))]">Thought</span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) =>
            <a
              key={link.href}
              href={link.href}
              className="nav-link text-sm font-medium text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]">

                {link.label}
              </a>
            )}
            
            {/* Search Box */}
            <div className="relative" ref={searchRef}>
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                data-testid="search-btn"
                className="p-2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
              >
                <Search size={20} />
              </button>
              
              {searchOpen && (
                <div className="absolute right-0 top-12 w-80 bg-[hsl(var(--background))] border border-[hsl(var(--border))] shadow-xl z-50">
                  <div className="p-3 border-b border-[hsl(var(--border))]">
                    <div className="relative">
                      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
                      <input
                        type="text"
                        placeholder="Search articles... (Ctrl+K)"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && searchQuery.trim()) {
                            navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
                            setSearchOpen(false);
                            setSearchQuery("");
                          }
                        }}
                        data-testid="search-input"
                        className="w-full pl-10 pr-4 py-2 bg-[hsl(var(--secondary))] border-none text-sm text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--primary))]"
                        autoFocus
                      />
                    </div>
                  </div>
                  
                  <div className="max-h-80 overflow-y-auto">
                    {searchQuery && searchResults.length === 0 && (
                      <div className="p-4 text-sm text-[hsl(var(--muted-foreground))] text-center">
                        No articles found
                      </div>
                    )}
                    {searchResults.slice(0, 5).map((result) => (
                      <button
                        key={result.slug}
                        onClick={() => handleResultClick(result.slug)}
                        data-testid={`search-result-${result.slug}`}
                        className="w-full text-left p-3 hover:bg-[hsl(var(--secondary))] transition-colors border-b border-[hsl(var(--border))] last:border-b-0"
                      >
                        <span className="text-xs font-mono text-[hsl(var(--primary))] uppercase">{result.category}</span>
                        <p className="text-sm text-[hsl(var(--foreground))] mt-1 line-clamp-2">{result.title}</p>
                      </button>
                    ))}
                    {searchQuery && searchResults.length > 0 && (
                      <button
                        onClick={() => {
                          navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
                          setSearchOpen(false);
                          setSearchQuery("");
                        }}
                        data-testid="search-view-all-btn"
                        className="w-full p-3 text-sm font-medium text-[hsl(var(--primary))] hover:bg-[hsl(var(--secondary))] transition-colors text-center border-t border-[hsl(var(--border))]"
                      >
                        View all {searchResults.length} results
                      </button>
                    )}
                    {!searchQuery && (
                      <div className="p-4 text-sm text-[hsl(var(--muted-foreground))] text-center">
                        Type to search news & articles
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <a
              href="#newsletter"
              data-testid="subscribe-nav-btn"
              className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] px-6 py-2 text-sm font-medium hover:bg-[hsl(var(--primary)/0.9)] transition-all">

              Subscribe
            </a>
          </div>

          {/* Mobile Search & Menu Buttons */}
          <div className="flex items-center gap-2 md:hidden">
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              data-testid="mobile-search-btn"
              className="p-2 text-[hsl(var(--foreground))]"
            >
              <Search size={22} />
            </button>
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-[hsl(var(--foreground))]">

              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Search Dropdown */}
        {searchOpen && (
          <div className="md:hidden absolute top-20 left-0 right-0 bg-[hsl(var(--background))] border-b border-[hsl(var(--border))] shadow-lg z-50" ref={searchRef}>
            <div className="p-4">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && searchQuery.trim()) {
                      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
                      setSearchOpen(false);
                      setSearchQuery("");
                    }
                  }}
                  data-testid="mobile-search-input"
                  className="w-full pl-10 pr-4 py-3 bg-[hsl(var(--secondary))] border-none text-base text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--primary))]"
                  autoFocus
                />
              </div>
            </div>
            
            <div className="max-h-64 overflow-y-auto">
              {searchQuery && searchResults.length === 0 && (
                <div className="p-4 text-sm text-[hsl(var(--muted-foreground))] text-center">
                  No articles found
                </div>
              )}
              {searchResults.slice(0, 5).map((result) => (
                <button
                  key={result.slug}
                  onClick={() => { handleResultClick(result.slug); setSearchOpen(false); }}
                  className="w-full text-left p-4 hover:bg-[hsl(var(--secondary))] transition-colors border-t border-[hsl(var(--border))]"
                >
                  <span className="text-xs font-mono text-[hsl(var(--primary))] uppercase">{result.category}</span>
                  <p className="text-sm text-[hsl(var(--foreground))] mt-1">{result.title}</p>
                </button>
              ))}
              {searchQuery && searchResults.length > 0 && (
                <button
                  onClick={() => {
                    navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
                    setSearchOpen(false);
                    setSearchQuery("");
                  }}
                  data-testid="mobile-search-view-all-btn"
                  className="w-full p-4 text-sm font-medium text-[hsl(var(--primary))] hover:bg-[hsl(var(--secondary))] transition-colors text-center border-t border-[hsl(var(--border))]"
                >
                  View all {searchResults.length} results
                </button>
              )}
              {!searchQuery && (
                <div className="p-4 text-sm text-[hsl(var(--muted-foreground))] text-center border-t border-[hsl(var(--border))]">
                  Type to search news & articles
                </div>
              )}
            </div>
          </div>
        )}

        {/* Mobile Menu */}
        {mobileMenuOpen &&
        <div className="md:hidden py-6 border-t border-white/5 animate-fade-in absolute top-20 left-0 right-0 bg-[hsl(var(--background))] z-50 px-6 shadow-lg">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) =>
            <a
              key={link.href}
              href={link.href}
              onClick={() => setMobileMenuOpen(false)}
              className="text-base font-medium text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] py-2">

                  {link.label}
                </a>
            )}
              <a
              href="#newsletter"
              onClick={() => setMobileMenuOpen(false)}
              className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] px-6 py-3 text-center font-medium mt-4">

                Subscribe
              </a>
            </div>
          </div>
        }
      </div>
    </nav>);

};

// Hero Section
const HeroSection = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden">

      {/* Background */}
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1517420704952-d9f39e95b43e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGJsdWUlMjBlbGVjdHJpYyUyMGNpcmN1aXQlMjB0ZWNobm9sb2d5JTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3Njk4NDg1NjR8MA&ixlib=rb-4.1.0&q=85')`
          }} />

        <div className="absolute inset-0 bg-gradient-to-b from-[hsl(var(--background))] via-[hsl(var(--background)/0.9)] to-[hsl(var(--background))]" />
        <div className="hero-glow absolute inset-0" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-32 md:py-40">
        <div className="max-w-4xl">
          <p className="text-sm font-mono tracking-wider uppercase text-[hsl(var(--primary))] mb-6 animate-fade-in-up opacity-0 stagger-1">
            B2B Technology Intelligence
          </p>
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-none mb-8 animate-fade-in-up opacity-0 stagger-2">
            Thought Leadership Insights That Shape{" "}
            <span className="gradient-text">Tomorrow's Technology</span>
          </h1>
          <p className="md:text-xl max-w-2xl animate-fade-in-up stagger-3 !text-lg mb-12 opacity-0 text-[hsl(var(--muted-foreground))]">Insightful coverage of AI, technology, sustainability, and the breakthroughs redefining tomorrow



          </p>
          <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up opacity-0 stagger-4">
            <a
              href="#news"
              data-testid="explore-insights-btn"
              className="btn-primary-custom inline-flex items-center justify-center gap-2 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] px-8 py-4 text-lg font-medium hover:bg-[hsl(var(--primary)/0.9)]">

              Explore Latest Insights
              <ArrowRight size={20} />
            </a>
            <a
              href="#about"
              data-testid="meet-writer-btn"
              className="inline-flex items-center justify-center gap-2 border border-[hsl(var(--border))] text-[hsl(var(--foreground))] px-8 py-4 text-lg font-medium hover:bg-[hsl(var(--secondary)/0.5)] transition-all">

              Meet the Writer
            </a>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronRight size={24} className="rotate-90 text-[hsl(var(--muted-foreground))]" />
        </div>
      </div>
    </section>);

};

// Latest News Section

const NewsCard = ({ news, featured }) =>
<article
  data-testid={`news-card-${news.id}`}
  className={`news-card group relative overflow-hidden bg-[hsl(var(--card))] border border-[hsl(var(--border))] hover:border-[hsl(var(--primary)/0.5)] transition-all duration-300 card-glow ${
  featured ? "md:col-span-2 md:row-span-2" : ""}`
  }>
  <Link to={`/article/${news.slug}`} className="block">
    <div className={`relative overflow-hidden ${featured ? "h-64 md:h-80" : "h-48"}`}>
      <img
      src={news.image}
      alt={news.title}
      loading="lazy"
      className="news-card-image w-full h-full object-cover" />

      <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--card))] to-transparent" />
    </div>
    <div className={`p-6 ${featured ? "md:p-8" : ""}`}>
      <div className="flex items-center gap-3 mb-4">
        <span className="text-xs font-mono tracking-wider uppercase text-[hsl(var(--primary))]">
          {news.category}
        </span>
        <span className="text-xs text-[hsl(var(--muted-foreground))]">{news.date}</span>
      </div>
      <h3
      className={`font-['Playfair_Display'] font-semibold mb-3 group-hover:text-[hsl(var(--primary))] transition-colors ${
      featured ? "text-2xl md:text-3xl" : "text-xl"}`
      }>

        {news.title}
      </h3>
      <p className="text-[hsl(var(--muted-foreground))] leading-relaxed line-clamp-3">
        {news.summary}
      </p>
      <span 
        className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-[hsl(var(--primary))] group-hover:gap-3 transition-all"
      >
        Read News <ArrowRight size={16} />
      </span>
    </div>
  </Link>
  </article>;


const NewsSection = () => {
  return (
    <section
      id="news"
      data-testid="news-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24">

      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <p className="text-sm font-mono tracking-wider uppercase text-[hsl(var(--primary))] mb-4">
              Technology Intelligence
            </p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight font-['Playfair_Display']">
              Latest Tech News
            </h2>
          </div>
          <p className="text-[hsl(var(--muted-foreground))] max-w-md">
            Stay ahead of global innovation with latest tech news, analysis, and insights from around the world.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {newsData.map((news, index) =>
          <NewsCard key={news.slug} news={news} featured={index === 0} />
          )}
        </div>
      </div>
    </section>);

};

// Thought Leadership Section
const iconMap = { Leaf, Lightbulb, Shield, Cpu };

const ThoughtLeadershipSection = () => {
  return (
    <section
      id="insights"
      data-testid="insights-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[hsl(var(--card)/0.3)]">

      <div className="max-w-7xl mx-auto">
        <div className="max-w-2xl mb-16">
          <p className="text-sm font-mono tracking-wider uppercase text-[hsl(var(--primary))] mb-4">
            Featured Articles
          </p>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight font-['Playfair_Display'] mb-6">
            Thought Leadership
          </h2>
          <p className="text-lg text-[hsl(var(--muted-foreground))] leading-relaxed">
            Original analysis and strategic commentary on the forces reshaping business
            and technology.
          </p>
        </div>

        <div className="space-y-8">
          {thoughtLeadershipData.map((essay) => {
          const IconComponent = iconMap[essay.icon] || Zap;
          return (
          <article
            key={essay.id}
            data-testid={`essay-${essay.id}`}
            className="essay-item group cursor-pointer">
            <Link to={`/article/${essay.slug}`} className="block">
              <div className="flex items-start gap-6">
                <div className="hidden md:flex items-center justify-center w-12 h-12 border border-[hsl(var(--border))] group-hover:border-[hsl(var(--primary))] transition-colors">
                  <IconComponent
                  size={24}
                  className="text-[hsl(var(--muted-foreground))] group-hover:text-[hsl(var(--primary))] transition-colors" />

                </div>
                <div className="flex-1">
                  <h3 className="text-2xl md:text-3xl font-['Playfair_Display'] font-medium mb-3 group-hover:text-[hsl(var(--primary))] transition-colors">
                    {essay.title}
                  </h3>
                  <p className="text-[hsl(var(--muted-foreground))] leading-relaxed max-w-3xl">
                    {essay.abstract}
                  </p>
                  <span 
                    className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-[hsl(var(--primary))] group-hover:gap-3 transition-all"
                  >
                    Read Full Article <ArrowRight size={16} />
                  </span>
                </div>
              </div>
            </Link>
            <Separator className="mt-8 bg-[hsl(var(--border))]" />
            </article>
          );
          })}
        </div>
      </div>
    </section>);

};



// About Section
const AboutSection = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24">

      <div className="max-w-7xl mx-auto">
        <div className="max-w-3xl mb-20">
          <p className="text-sm font-mono tracking-wider uppercase text-[hsl(var(--primary))] mb-4">
            About Us
          </p>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight font-['Playfair_Display'] mb-6" data-testid="about-us-heading">
            FutureThought
          </h2>
          <div className="space-y-6 text-lg text-[hsl(var(--muted-foreground))] leading-relaxed text-justify">
            <p>
              FutureThought is where the next era of innovation takes shape. We deliver sharp, research-driven coverage on AI, emerging technologies, sustainability and the breakthroughs redefining how the world works.
            </p>
            <p>
              Our mission is simple. Turn complexity into clarity. We translate fast-moving developments into intelligence leaders can act on, innovators can build with and curious minds can use to stay ahead of change.
            </p>
            <p>
              Through accessible storytelling and deep analysis, FutureThought highlights the ideas, trends and technologies shaping the future of business, society and the planet.
            </p>
          </div>
        </div>

        <div className="max-w-3xl">
          <p className="text-sm font-mono tracking-wider uppercase text-[hsl(var(--primary))] mb-4">
            About the Writer
          </p>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight font-['Playfair_Display'] mb-6">
            Jit Singh
          </h2>
          <div className="space-y-6 text-lg text-[hsl(var(--muted-foreground))] leading-relaxed text-justify">
            <p>
              Strategic communication and thought leadership specialist with expertise in 
              B2B technology, energy and sustainability sector.
            </p>
            <p>
              With five years of thought leadership research and communication experience
              at a leading energy technology company, I bring a unique perspective to the
              intersection of technology innovation and business strategy.
            </p>
            <p>My work focuses on translating complex technical developments into clear, actionable intelligence for different business need. I specialize in ESG communications, emerging technology analysis, and research-driven content that drives business outcomes.




            </p>
          </div>
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <a
              href="https://www.linkedin.com/in/jit-singh-68ba6716a/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="linkedin-btn"
              className="inline-flex items-center justify-center gap-2 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] px-6 py-3 font-medium hover:bg-[hsl(var(--primary)/0.9)] transition-all">
              <Linkedin size={18} />
              Connect on LinkedIn
            </a>
            <a
              href="#services"
              data-testid="view-services-btn"
              className="inline-flex items-center justify-center gap-2 border border-[hsl(var(--border))] text-[hsl(var(--foreground))] px-6 py-3 font-medium hover:bg-[hsl(var(--secondary)/0.5)] transition-all">
              View Services
            </a>
          </div>
        </div>
      </div>
    </section>);

};

// Services Section
const servicesData = [
{
  id: 1,
  icon: MessageSquare,
  title: "Strategic Communications",
  description:
  "Executive messaging, stakeholder communications, and thought leadership positioning that resonates with B2B audiences."
},
{
  id: 2,
  icon: Leaf,
  title: "ESG & Sustainability Storytelling",
  description:
  "Compelling narratives that translate sustainability initiatives into business value for investors, customers, and employees."
},
{
  id: 3,
  icon: Cpu,
  title: "Technology Thought Leadership",
  description:
  "Original analysis and content that establishes your organization as a voice of authority in emerging technology domains."
},
{
  id: 4,
  icon: BookOpen,
  title: "Research-Driven Content",
  description:
  "White papers, industry reports, and data-driven content that supports business development and market positioning."
}];


const ServicesSection = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[hsl(var(--card)/0.3)]">

      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-sm font-mono tracking-wider uppercase text-[hsl(var(--primary))] mb-4">
            Freelance Services
          </p>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight font-['Playfair_Display'] mb-6">
            Work With Me
          </h2>
          <p className="text-lg text-[hsl(var(--muted-foreground))] leading-relaxed">
            Strategic content and communications services for B2B technology organizations.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {servicesData.map((service) =>
          <div
            key={service.id}
            data-testid={`service-card-${service.id}`}
            className="service-card p-8 bg-[hsl(var(--card)/0.5)]">

              <div className="w-12 h-12 flex items-center justify-center border border-[hsl(var(--border))] mb-6">
                <service.icon size={24} className="text-[hsl(var(--primary))]" />
              </div>
              <h3 className="text-xl font-['Playfair_Display'] font-semibold mb-3">
                {service.title}
              </h3>
              <p className="text-[hsl(var(--muted-foreground))] leading-relaxed">
                {service.description}
              </p>
            </div>
          )}
        </div>

        <div className="text-center mt-12">
          <a
            href="https://www.linkedin.com/in/jit-singh-68ba6716a/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="book-consultation-btn"
            className="inline-flex items-center justify-center gap-2 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] px-8 py-4 text-lg font-medium hover:bg-[hsl(var(--primary)/0.9)] transition-all">

            Book a Consultation
            <ArrowRight size={20} />
          </a>
        </div>
      </div>
    </section>);

};

// Newsletter Section
const NewsletterSection = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, { email });
      if (response.data.success) {
        toast.success("Successfully subscribed! Welcome to FutureThought.");
        setEmail("");
      }
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="newsletter"
      data-testid="newsletter-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[hsl(var(--primary))]">

      <div className="max-w-7xl mx-auto">
        <div className="max-w-2xl mx-auto text-center">
          <Zap size={40} className="mx-auto mb-6 text-[hsl(var(--primary-foreground))]" />
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight font-['Playfair_Display'] mb-6 text-[hsl(var(--primary-foreground))]">
            Stay Ahead of the Curve
          </h2>
          <p className="text-lg text-[hsl(var(--primary-foreground)/0.9)] leading-relaxed mb-8">
            Get weekly insights on B2B technology, AI, sustainability, and digital
            transformation delivered directly to your inbox. Join decision-makers who
            demand clarity.
          </p>

          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="newsletter-email-input"
              className="h-12 bg-white/10 border-white/20 text-[hsl(var(--primary-foreground))] placeholder:text-[hsl(var(--primary-foreground)/0.6)] focus:border-white" />

            <Button
              type="submit"
              disabled={loading}
              data-testid="newsletter-submit-btn"
              className="h-12 px-8 bg-[hsl(var(--primary-foreground))] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-foreground)/0.9)] font-medium">

              {loading ? "Subscribing..." : "Subscribe"}
              <Send size={18} className="ml-2" />
            </Button>
          </form>

          <p className="mt-6 text-sm text-[hsl(var(--primary-foreground)/0.7)]">
            No spam. Unsubscribe anytime. Your privacy matters.
          </p>
        </div>
      </div>
    </section>);

};

// Footer
const Footer = () => {
  return (
    <footer
      data-testid="footer"
      className="py-16 px-6 md:px-12 lg:px-24 border-t border-[hsl(var(--border))]">

      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <a href="#" className="text-2xl font-bold font-['Playfair_Display'] mb-4 inline-block">
              Future<span className="text-[hsl(var(--primary))]">Thought</span>
            </a>
            <p className="text-[hsl(var(--muted-foreground))] leading-relaxed max-w-md text-justify">FutureThought is a hub for B2B technology intelligence and thought leadership, delivering sharp insights on AI, sustainability and the emerging technologies redefining how business and society evolve.


            </p>
          </div>

          <div>
            <h4 className="font-semibold mb-4">Explore</h4>
            <ul className="space-y-3">
              <li>
                <a href="#news" className="footer-link text-[hsl(var(--muted-foreground))]">
                  Latest News
                </a>
              </li>
              <li>
                <a href="#insights" className="footer-link text-[hsl(var(--muted-foreground))]">
                  Thought Leadership
                </a>
              </li>
              <li>
                <a href="#about" className="footer-link text-[hsl(var(--muted-foreground))]">
                  About
                </a>
              </li>
              <li>
                <a href="#services" className="footer-link text-[hsl(var(--muted-foreground))]">
                  Services
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4">Connect</h4>
            <ul className="space-y-3">
              <li>
                <a
                  href="https://www.linkedin.com/in/jit-singh-68ba6716a/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-link text-[hsl(var(--muted-foreground))] inline-flex items-center gap-2">

                  <Linkedin size={16} /> LinkedIn
                </a>
              </li>
              <li>
                <a
                  href="https://www.linkedin.com/company/futurethought/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-link text-[hsl(var(--muted-foreground))] inline-flex items-center gap-2"
                  data-testid="futurethought-linkedin-btn">
                  <Linkedin size={16} /> FutureThought
                </a>
              </li>
              <li>
                <a
                  href={`${BACKEND_URL}/api/rss`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-link text-[hsl(var(--muted-foreground))] inline-flex items-center gap-2"
                  data-testid="rss-feed-btn">
                  <Rss size={16} /> RSS Feed
                </a>
              </li>
            </ul>
          </div>
        </div>

        <Separator className="my-12 bg-[hsl(var(--border))]" />

        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            © {new Date().getFullYear()} FutureThought. All rights reserved.
          </p>
          <div className="flex gap-6 text-sm text-[hsl(var(--muted-foreground))]">
            <Link to="/privacy-policy" className="footer-link" data-testid="footer-privacy-link">
              Privacy Policy
            </Link>
            <Link to="/terms" className="footer-link" data-testid="footer-terms-link">
              Terms of Service
            </Link>
            <Link to="/about" className="footer-link" data-testid="footer-about-link">
              About
            </Link>
            <Link to="/contact" className="footer-link" data-testid="footer-contact-link">
              Contact
            </Link>
          </div>
        </div>
      </div>
    </footer>);

};

// Main Landing Page Component
const LandingPage = () => {
  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <Helmet>
        <title>FutureThought | B2B Technology Intelligence</title>
        <meta name="description" content="Thought leadership insights on AI, technology, sustainability, and the breakthroughs shaping tomorrow. B2B technology intelligence by Jit Singh." />
        <meta name="keywords" content="AI, artificial intelligence, semiconductor, sustainability, B2B technology, thought leadership, enterprise technology, Jit Singh, FutureThought" />
        <link rel="canonical" href="https://futurethought.emergent.host/" />
        <link rel="alternate" type="application/rss+xml" title="FutureThought RSS Feed" href="https://futurethought.emergent.host/api/rss" />

        <meta property="og:type" content="website" />
        <meta property="og:title" content="FutureThought | B2B Technology Intelligence" />
        <meta property="og:description" content="Thought leadership insights on AI, technology, sustainability, and the breakthroughs shaping tomorrow." />
        <meta property="og:url" content="https://futurethought.emergent.host/" />
        <meta property="og:site_name" content="FutureThought" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="FutureThought | B2B Technology Intelligence" />
        <meta name="twitter:description" content="Thought leadership insights on AI, technology, sustainability, and the breakthroughs shaping tomorrow." />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "FutureThought",
            "url": "https://futurethought.emergent.host",
            "description": "Thought leadership insights on AI, technology, sustainability, and the breakthroughs shaping tomorrow. B2B technology intelligence by Jit Singh.",
            "publisher": {
              "@type": "Organization",
              "name": "FutureThought",
              "url": "https://futurethought.emergent.host"
            },
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://futurethought.emergent.host/search?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "FutureThought",
            "url": "https://futurethought.emergent.host",
            "description": "B2B technology intelligence and thought leadership",
            "founder": {
              "@type": "Person",
              "name": "Jit Singh"
            },
            "sameAs": [
              "https://www.linkedin.com/company/futurethought"
            ]
          })}
        </script>
      </Helmet>
      <Navigation />
      <HeroSection />
      
      <NewsSection />
      <ThoughtLeadershipSection />
      <AboutSection />
      <ServicesSection />
      
      <NewsletterSection />
      <Footer />
    </div>);

};

export default LandingPage;